import React from 'react';
import { Plane, Plus, Edit, Trash2, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useInView } from 'react-intersection-observer';

interface Airport {
  id: string;
  code: string;
  city: string;
  airport: string;
  country: string;
}

export function AirportsScreen() {
  const [airports, setAirports] = React.useState<Airport[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingAirport, setEditingAirport] = React.useState<Airport | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const { ref: loadMoreRef, inView } = useInView();

  const [form, setForm] = React.useState({
    code: '',
    city: '',
    airport: '',
    country: '',
  });

  const loadAirports = React.useCallback(async (pageNumber: number, search: string = '') => {
    try {
      setIsFetchingMore(true);
      const from = pageNumber * 50;
      const to = from + 49;

      let query = supabase
        .from('airports')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.or(`city.ilike.%${search}%,airport.ilike.%${search}%,code.ilike.%${search}%`);
      }

      const { data, error: fetchError, count } = await query
        .order('city', { ascending: true })
        .range(from, to);

      if (fetchError) throw fetchError;

      if (pageNumber === 0) {
        setAirports(data || []);
      } else {
        setAirports(prev => [...prev, ...(data || [])]);
      }

      setHasMore(count ? from + 50 < count : false);
    } catch (err) {
      console.error('Error loading airports:', err);
      setError('Erro ao carregar aeroportos');
    } finally {
      setIsFetchingMore(false);
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAirports(0, searchTerm);
    setPage(0);
  }, [loadAirports, searchTerm]);

  React.useEffect(() => {
    if (inView && hasMore && !isFetchingMore && !isLoading) {
      setPage(prev => {
        const nextPage = prev + 1;
        loadAirports(nextPage, searchTerm);
        return nextPage;
      });
    }
  }, [inView, hasMore, isFetchingMore, isLoading, loadAirports, searchTerm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (isEditing && editingAirport) {
        const { error: updateError } = await supabase
          .from('airports')
          .update(form)
          .eq('id', editingAirport.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('airports')
          .insert([form]);

        if (insertError) throw insertError;
      }

      setForm({ code: '', city: '', airport: '', country: '' });
      setIsEditing(false);
      setEditingAirport(null);
      loadAirports(0);
      setPage(0);
    } catch (err) {
      console.error('Error saving airport:', err);
      setError('Erro ao salvar aeroporto');
    }
  };

  const handleEdit = (airport: Airport) => {
    setIsEditing(true);
    setEditingAirport(airport);
    setForm({
      code: airport.code,
      city: airport.city,
      airport: airport.airport,
      country: airport.country,
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este aeroporto?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('airports')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      loadAirports(0);
      setPage(0);
    } catch (err) {
      console.error('Error deleting airport:', err);
      setError('Erro ao excluir aeroporto');
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Plane className="text-blue-600" size={24} />
          {isEditing ? 'Editar Aeroporto' : 'Novo Aeroporto'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código IATA
              </label>
              <input
                required
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                maxLength={3}
                placeholder="GRU"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cidade
              </label>
              <input
                required
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="São Paulo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aeroporto
              </label>
              <input
                required
                type="text"
                value={form.airport}
                onChange={(e) => setForm({ ...form, airport: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Aeroporto Internacional de Guarulhos"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                País
              </label>
              <input
                required
                type="text"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Brasil"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingAirport(null);
                  setForm({ code: '', city: '', airport: '', country: '' });
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              {isEditing ? (
                <>
                  <Edit size={20} />
                  Atualizar
                </>
              ) : (
                <>
                  <Plus size={20} />
                  Adicionar
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Aeroportos Cadastrados ({airports.length})
        </h2>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar aeroporto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="space-y-4">
          {airports.map((airport) => (
            <div
              key={airport.id}
              className="bg-gray-50 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-blue-600">{airport.code}</span>
                    <span className="text-gray-600">•</span>
                    <span className="font-medium text-gray-900">{airport.city}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{airport.airport}</p>
                  <p className="text-sm text-gray-500">{airport.country}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(airport)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar aeroporto"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(airport.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Excluir aeroporto"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Infinite scroll trigger */}
          {hasMore && (
            <div ref={loadMoreRef} className="py-4 text-center">
              {isFetchingMore ? 'Carregando mais...' : ''}
            </div>
          )}

          {!isLoading && airports.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum aeroporto encontrado
            </div>
          )}
        </div>
      </div>
    </div>
  );
}